import React, { useEffect, useState } from 'react';
import { Video, Phone } from 'lucide-react';

/**
 * FaceTime deep-link buttons for iPhone / iPad / Mac.
 *
 * Apple URL schemes:
 *   facetime:{number-or-email}        → video call
 *   facetime-audio:{number-or-email}  → audio-only call
 *
 * On non-Apple platforms (Android, Windows, Linux) the button renders as a
 * disabled chip explaining why — FaceTime isn't available there.
 */
function isAppleDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iOS Safari, iPadOS Safari, macOS Safari/Chrome/Firefox all match one of these.
  // iPadOS 13+ reports as MacIntel — combine with maxTouchPoints to catch it.
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua);
  const isIPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return isIOS || isMac || isIPadOS;
}

export function FaceTimeButtons({ contact, label = '', size = 'sm', className = '' }) {
  // ``contact`` is a phone number OR an email — FaceTime supports both.
  const [isApple, setIsApple] = useState(false);

  useEffect(() => { setIsApple(isAppleDevice()); }, []);

  if (!contact) return null;

  // E.164 / digits-only or email — pass through unchanged; FaceTime is lenient.
  const target = encodeURIComponent(contact.trim());
  const isCompact = size === 'compact';
  const px = isCompact ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const iconSize = isCompact ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const textSize = isCompact ? 'text-[10px]' : 'text-xs';

  if (!isApple) {
    return (
      <span
        className={`inline-flex items-center gap-1 ${px} ${textSize} text-slate-400 border border-slate-200 bg-slate-50 rounded-sm cursor-not-allowed ${className}`}
        title="FaceTime is only available on iPhone, iPad, and Mac"
        data-testid="facetime-unavailable"
      >
        <Video className={iconSize} /> FaceTime
      </span>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1 ${className}`} data-testid="facetime-buttons">
      <a
        href={`facetime:${target}`}
        className={`inline-flex items-center gap-1 ${px} ${textSize} font-medium rounded-sm border border-[#5856D6]/40 text-[#5856D6] bg-[#5856D6]/5 hover:bg-[#5856D6]/10 transition-colors`}
        title="Start FaceTime video call"
        data-testid="facetime-video-btn"
      >
        <Video className={iconSize} />
        {label && <span>FaceTime</span>}
      </a>
      <a
        href={`facetime-audio:${target}`}
        className={`inline-flex items-center gap-1 ${px} ${textSize} font-medium rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors`}
        title="Start FaceTime audio call"
        data-testid="facetime-audio-btn"
      >
        <Phone className={iconSize} />
      </a>
    </div>
  );
}
